/**
 * Which calling system this server runs: `legacy` (today's) or `v2`. The server decides
 * (`GET /api/v2/calls/config`); the browser only asks. Part of the temporary switch between
 * the two systems, deleted at cut-over together with the old one.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import type { CallsConfigResponse } from '@jema-manager/shared';
import { ApiClientError, apiClient } from '@shared/api/api-client';
import { useAuthStore } from '@shared/stores/auth-store';

export type CallEngine = 'legacy' | 'v2';

const STORAGE_KEY = 'jema.calls.engine';

const readRemembered = (): CallEngine | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'v2' || value === 'legacy' ? value : null;
  } catch {
    return null;
  }
};

/** Admins and super admins take calls; nobody else ever sees the new system. */
export const isCallStaffRole = (role: string | undefined | null): boolean =>
  role === 'admin' || role === 'super_admin';

interface EngineState {
  /** `null`: not known yet (first visit of this browser, the server has not answered). */
  engine: CallEngine | null;
  /** The v2 dock has a line or an audio leg open: the engine must not change under it. */
  v2Live: boolean;
  /** The server's newer answer, applied the moment the last v2 line is gone. */
  pending: CallEngine | null;
}

export const useCallEngineStore = create<EngineState>(() => ({
  engine: readRemembered(),
  v2Live: false,
  pending: null,
}));

const setEngine = (engine: CallEngine, remember: boolean): void => {
  const state = useCallEngineStore.getState();
  // Never take the dock away from under a live call: the answer waits until the line is gone.
  if (state.engine === 'v2' && engine !== 'v2' && state.v2Live)
    useCallEngineStore.setState({ pending: engine });
  else useCallEngineStore.setState({ engine, pending: null });
  if (!remember) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, engine);
  } catch {
    // Without storage the next page load simply asks again.
  }
};

/** Told by the v2 shell whenever it gains or loses its last line. */
export const setV2Live = (v2Live: boolean): void => {
  const { pending } = useCallEngineStore.getState();
  useCallEngineStore.setState(
    !v2Live && pending ? { v2Live, engine: pending, pending: null } : { v2Live }
  );
};

/** Sign-out: the next person on this browser asks the server again. */
export const forgetCallEngine = (): void => {
  useCallEngineStore.setState({ engine: null, pending: null });
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was stored.
  }
};

// ponytail: tests replace the auth store with a bare selector function, hence the guard.
if (typeof useAuthStore.subscribe === 'function')
  useAuthStore.subscribe((state, previous) => {
    if (previous.user && !state.user) forgetCallEngine();
  });

/** Only the server switching the new system off counts as it saying "legacy". */
const LEGACY_CODES: ReadonlySet<string | undefined> = new Set([
  'calls_not_enabled',
  'calls_not_configured',
]);

/**
 * The server's answer: `engine` in a 200, or a 503 that says the new system is off or not set
 * up (`calls_not_enabled`, `calls_not_configured`). Everything else (no network, our own 4 s
 * timeout, a proxy's 502, a 500, an expired session, a rate limit) is NO answer and throws, so
 * the browser keeps the engine it had.
 */
export async function fetchCallEngine(): Promise<CallEngine> {
  try {
    const response = await apiClient.get<{ data: CallsConfigResponse }>(
      '/calls/config',
      undefined,
      { timeout: 4_000, dedupe: 'off', requestLabel: 'calls:engine' }
    );
    return response.data.engine === 'v2' ? 'v2' : 'legacy';
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 503 && LEGACY_CODES.has(error.code))
      return 'legacy';
    throw error;
  }
}

/** Asks the server (staff only) and keeps the store current. Mounted once, by the app shell. */
export function useResolveCallEngine(enabled: boolean): void {
  const query = useQuery({
    queryKey: ['calls-engine'],
    queryFn: fetchCallEngine,
    enabled,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    // No answer yet: ask again quietly rather than leave the browser on a guess all day.
    refetchInterval: current => (current.state.status === 'error' ? 60_000 : false),
  });
  React.useEffect(() => {
    if (query.data) setEngine(query.data, true);
    // No answer and nothing remembered: today's system, until the server says otherwise.
    else if (query.isError && useCallEngineStore.getState().engine === null)
      setEngine('legacy', false);
  }, [query.data, query.isError]);
}

/** The engine this user gets: people who are not call staff always get today's system. */
export function useCallEngine(): CallEngine | null {
  const role = useAuthStore(state => state.user?.role);
  const engine = useCallEngineStore(state => state.engine);
  return isCallStaffRole(role) ? engine : 'legacy';
}
